// EditFuelModal component - Modal for editing fuel record data

import { useState, useEffect } from "react";
import Modal from "./Modal";
import SearchableSelect from "../common/SearchableSelect";
import { getVehicles } from "../../services/api";

const EditFuelModal = ({ isOpen, onClose, onSubmit, record }) => {
  const [formData, setFormData] = useState({
    license_plate: "",
    date: "",
    amount: "",
    oil_price: "",
    oil_volume: "",
    gas_station: "",
    mileage: "",
  });

  const [hasMileage, setHasMileage] = useState(true);
  const [errors, setErrors] = useState({});
  const [vehicles, setVehicles] = useState([]);
  const [loadingVehicles, setLoadingVehicles] = useState(true);

  // Fetch vehicles from API
  useEffect(() => {
    const fetchVehicles = async () => {
      setLoadingVehicles(true);
      try {
        const result = await getVehicles();
        if (result.success && result.data) {
          setVehicles(result.data);
        }
      } catch (error) {
        console.error("Error fetching vehicles:", error);
      } finally {
        setLoadingVehicles(false);
      }
    };
    fetchVehicles();
  }, []);

  useEffect(() => {
    if (record) {
      const mileageVal = record.mileage;
      const hasMil = mileageVal !== null && mileageVal !== undefined && parseFloat(mileageVal) > 0;
      setHasMileage(hasMil);
      setFormData({
        license_plate: record.license_plate || "",
        date: record.date || "",
        amount: record.amount?.toString() || "",
        oil_price: record.oil_price?.toString() || "",
        oil_volume: record.oil_volume?.toString() || "",
        gas_station: record.gas_station || "",
        mileage: hasMil ? mileageVal.toString() : "",
      });
      setErrors({});
    }
  }, [record]);

  const handleInputChange = (e) => {
    const { name } = e.target;
    let { value } = e.target;

    // Clamp oil_price in real-time: max 60
    if (name === "oil_price" && value !== "") {
      const num = parseFloat(value);
      if (!isNaN(num) && num > 60) value = "60";
    }

    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));

    // Clear error for this field
    if (errors[name]) {
      setErrors((prev) => ({
        ...prev,
        [name]: "",
      }));
    }

    // Auto-calculate oil_volume when amount and oil_price are provided
    if (name === "amount" || name === "oil_price") {
      const amount =
        name === "amount" ? parseFloat(value) : parseFloat(formData.amount);
      const price =
        name === "oil_price"
          ? parseFloat(value)
          : parseFloat(formData.oil_price);

      if (!isNaN(amount) && !isNaN(price) && price > 0) {
        const rawVolume = amount / price;
        const volume = Math.min(rawVolume, 100).toFixed(2);
        setFormData((prev) => ({
          ...prev,
          [name]: value,
          oil_volume: volume,
        }));
      }
    }
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData.license_plate.trim()) {
      newErrors.license_plate = "กรุณากรอกป้ายทะเบียน";
    }
    if (!formData.date) {
      newErrors.date = "กรุณาเลือกวันที่";
    }
    if (!formData.amount || parseFloat(formData.amount) <= 0) {
      newErrors.amount = "กรุณากรอกจำนวนเงินที่ถูกต้อง";
    }
    if (!formData.oil_price || parseFloat(formData.oil_price) <= 0) {
      newErrors.oil_price = "กรุณากรอกราคาน้ำมันที่ถูกต้อง";
    }
    if (!formData.gas_station.trim()) {
      newErrors.gas_station = "กรุณากรอกชื่อปั๊มน้ำมัน";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (e) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    const data = {
      license_plate: formData.license_plate.trim(),
      date: formData.date,
      amount: parseFloat(formData.amount) || 0,
      oil_price: parseFloat(formData.oil_price) || 0,
      oil_volume: parseFloat(formData.oil_volume) || 0,
      gas_station: formData.gas_station.trim(),
      mileage: hasMileage ? (parseFloat(formData.mileage) || 0) : null,
    };

    onSubmit(record.id, data);
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title="แก้ไขข้อมูลการเติมน้ำมัน"
      size="lg"
    >
      <form onSubmit={handleSubmit} className="space-y-5">
        {/* License Plate and Date Row */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {/* License Plate */}
          <div>
            <SearchableSelect
              name="license_plate"
              value={formData.license_plate}
              onChange={handleInputChange}
              options={vehicles}
              placeholder={loadingVehicles ? "กำลังโหลด..." : "เลือกป้ายทะเบียน..."}
              label="ป้ายทะเบียน *"
              disabled={loadingVehicles}
              icon={
                <svg className="w-4 h-4 text-white" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M6 2a1 1 0 00-1 1v1H4a2 2 0 00-2 2v10a2 2 0 002 2h12a2 2 0 002-2V6a2 2 0 00-2-2h-1V3a1 1 0 10-2 0v1H7V3a1 1 0 00-1-1zm0 5a1 1 0 000 2h8a1 1 0 100-2H6z" clipRule="evenodd" />
                </svg>
              }
            />
            {errors.license_plate && (
              <p className="mt-1 text-sm text-red-500">
                {errors.license_plate}
              </p>
            )}
          </div>

          {/* Date */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              วันที่ <span className="text-red-500">*</span>
            </label>
            <input
              type="date"
              name="date"
              value={formData.date}
              onChange={handleInputChange}
              className={`w-full px-4 py-2.5 border ${
                errors.date ? "border-red-500" : "border-gray-300"
              } rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors`}
            />
            {errors.date && (
              <p className="mt-1 text-sm text-red-500">{errors.date}</p>
            )}
          </div>
        </div>

        {/* Amount and Oil Price Row */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {/* Amount */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              จำนวนเงิน (บาท) <span className="text-red-500">*</span>
            </label>
            <div className="relative">
              <input
                type="number"
                name="amount"
                value={formData.amount}
                onChange={handleInputChange}
                placeholder="0.00"
                step="0.01"
                min="0"
                className={`w-full px-4 py-2.5 border ${
                  errors.amount ? "border-red-500" : "border-gray-300"
                } rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors`}
              />
              <span className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500">
                ฿
              </span>
            </div>
            {errors.amount && (
              <p className="mt-1 text-sm text-red-500">{errors.amount}</p>
            )}
          </div>

          {/* Oil Price */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              ราคาน้ำมัน/ลิตร (บาท) <span className="text-red-500">*</span>
            </label>
            <div className="relative">
              <input
                type="number"
                name="oil_price"
                value={formData.oil_price}
                onChange={handleInputChange}
                placeholder="20-60"
                step="0.01"
                min="20"
                max="60"
                className={`w-full px-4 py-2.5 border ${
                  errors.oil_price ? "border-red-500" : "border-gray-300"
                } rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors`}
              />
              <span className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500">
                ฿/L
              </span>
            </div>
            {errors.oil_price && (
              <p className="mt-1 text-sm text-red-500">{errors.oil_price}</p>
            )}
          </div>
        </div>

        {/* Oil Volume (Auto-calculated) */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            ปริมาณน้ำมัน (ลิตร)
            <span className="text-gray-500 text-xs ml-2">(คำนวณอัตโนมัติ)</span>
          </label>
          <div className="relative">
            <input
              type="number"
              name="oil_volume"
              value={formData.oil_volume}
              onChange={handleInputChange}
              placeholder="0.00"
              step="0.01"
              min="0"
              max="100"
              className="w-full px-4 py-2.5 border border-gray-300 rounded-lg bg-gray-50 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
            />
            <span className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500">
              L
            </span>
          </div>
        </div>

        {/* Gas Station and Mileage Row */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {/* Gas Station */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              ปั๊มน้ำมัน <span className="text-red-500">*</span>
            </label>
            <div className="flex gap-3">
              {[
                { value: "บางจาก", label: "บางจาก" },
                { value: "ปตท", label: "ปตท" },
                { value: "ฉลอง", label: "ฉลอง" },
              ].map((opt) => (
                <label
                  key={opt.value}
                  className={`flex-1 flex items-center justify-center gap-2 px-4 py-2.5 rounded-lg border-2 cursor-pointer transition-all duration-200 font-medium text-sm ${
                    formData.gas_station === opt.value
                      ? "border-blue-500 bg-blue-50 text-blue-700"
                      : `border-gray-300 bg-white text-gray-600 hover:border-blue-300 ${
                          errors.gas_station ? "border-red-400" : ""
                        }`
                  }`}
                >
                  <input
                    type="radio"
                    name="gas_station"
                    value={opt.value}
                    checked={formData.gas_station === opt.value}
                    onChange={handleInputChange}
                    className="accent-blue-500"
                  />
                  ⛽ {opt.label}
                </label>
              ))}
            </div>
            {errors.gas_station && (
              <p className="mt-1 text-sm text-red-500">{errors.gas_station}</p>
            )}
          </div>

          {/* Mileage */}
          <div>
            <div className="flex items-center justify-between mb-2">
              <label className="block text-sm font-medium text-gray-700">
                เลขไมล์ (กม.)
              </label>
              <label className="flex items-center gap-1.5 cursor-pointer select-none">
                <input
                  type="checkbox"
                  checked={hasMileage}
                  onChange={(e) => {
                    setHasMileage(e.target.checked);
                    if (!e.target.checked) {
                      setFormData((prev) => ({ ...prev, mileage: "" }));
                    }
                  }}
                  className="w-4 h-4 accent-blue-500 cursor-pointer"
                />
                <span className="text-xs text-gray-500">มีเลขไมล์</span>
              </label>
            </div>
            <div className="relative">
              <input
                type="number"
                name="mileage"
                value={formData.mileage}
                onChange={handleInputChange}
                placeholder={hasMileage ? "0" : "ไม่มีเลขไมล์"}
                min="0"
                disabled={!hasMileage}
                className={`w-full px-4 py-2.5 border rounded-lg transition-colors ${
                  hasMileage
                    ? "border-gray-300 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    : "border-gray-200 bg-gray-100 text-gray-400 cursor-not-allowed"
                }`}
              />
              <span className={`absolute right-3 top-1/2 -translate-y-1/2 ${hasMileage ? "text-gray-500" : "text-gray-300"}`}>
                km
              </span>
            </div>
          </div>
        </div>

        {/* Info Box */}
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
          <div className="flex items-start">
            <svg
              className="w-5 h-5 text-blue-600 mt-0.5 mr-3 flex-shrink-0"
              fill="currentColor"
              viewBox="0 0 20 20"
            >
              <path
                fillRule="evenodd"
                d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z"
                clipRule="evenodd"
              />
            </svg>
            <div className="text-sm text-blue-800">
              <p className="font-medium mb-1">คำแนะนำ:</p>
              <ul className="list-disc list-inside space-y-1 text-blue-700">
                <li>ปริมาณน้ำมันจะคำนวณอัตโนมัติจากจำนวนเงินและราคาต่อลิตร</li>
                <li>ฟิลด์ที่มีเครื่องหมาย * จำเป็นต้องกรอก</li>
              </ul>
            </div>
          </div>
        </div>

        {/* Action Buttons */}
        <div className="flex gap-3 justify-end pt-4 border-t border-gray-200">
          <button
            type="button"
            onClick={onClose}
            className="px-6 py-2.5 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 font-medium transition-colors duration-200"
          >
            ยกเลิก
          </button>
          <button
            type="submit"
            className="px-6 py-2.5 bg-gradient-to-r from-blue-600 to-blue-700 text-white rounded-lg hover:from-blue-700 hover:to-blue-800 font-medium transition-all duration-200 shadow-lg shadow-blue-500/30"
          >
            บันทึกการแก้ไข
          </button>
        </div>
      </form>
    </Modal>
  );
};

export default EditFuelModal;
